import { SUPPORTED_LANGUAGES, type SupportedLanguageCode } from '@/constants/languages';

/**
 * Map the active i18n language to a date-formatting locale. Mirrors the mobile
 * app's `src/utils/dateLocale.ts`, so a deadline reads the same on both clients.
 *
 * Two things matter for a ballot deadline, and only one of them is cosmetic.
 *
 * **Calendar.** Every entry forces `-u-ca-gregory`. Persian defaults to the
 * Solar Hijri calendar, so a plain `fa` renders 2026-11-03 as `۱۲ آبان ۱۴۰۵` —
 * a different date in a different era, which no voter could reconcile against
 * their polling notice. Pinning the calendar keeps the day the same day in
 * every locale; only the language of the month name changes.
 *
 * **Ordering and clock.** Left to the locale. `es-US` renders numerically as
 * `3/11/2026` (day-first, CLDR's data for Spanish-in-the-US) and keeps a
 * 12-hour clock, where a bare `es` uses a 24-hour one. Because `3/11` and
 * `11/3` are both plausible readings of an election date, prefer a format with
 * a spelled-out month at every call site that shows a deadline; those are
 * unambiguous in all 16 locales.
 *
 * `en-US` and `es-US` are the only `-US` pairings ICU actually recognizes;
 * elsewhere the region tag is a no-op, so the base tag is used instead of
 * implying a precision that does not exist.
 */
const DATE_LOCALES: Record<SupportedLanguageCode, string> = {
  en: 'en-US',
  es: 'es-US',
  'zh-Hans': 'zh-Hans',
  'zh-Hant': 'zh-Hant',
  vi: 'vi',
  ko: 'ko',
  // Tagalog has no CLDR date data; Filipino is its closest available match and
  // is what ICU resolves `tl` to anyway.
  tl: 'fil',
  km: 'km',
  bn: 'bn',
  ja: 'ja',
  ar: 'ar',
  fa: 'fa',
  ur: 'ur',
  ru: 'ru',
  // Haitian Creole has no CLDR date data, so a bare `ht` falls back to the
  // browser's own locale. English matches the mobile app.
  ht: 'en-US',
  pt: 'pt',
};

const GREGORIAN = '-u-ca-gregory';
const FALLBACK = `en-US${GREGORIAN}`;

/**
 * Resolve a date locale for the active language.
 *
 * Accepts any tag i18next might hold — region-tagged, differently cased — and
 * matches it back to a registry code, falling back to English rather than
 * throwing on an unknown value.
 */
export const dateLocale = (lang: string): string => {
  if (!lang) return FALLBACK;
  const normalized = lang.trim().toLowerCase();

  const match = SUPPORTED_LANGUAGES.find(
    (l) => l.code.toLowerCase() === normalized || normalized.startsWith(`${l.code.toLowerCase()}-`)
  );
  if (match) return `${DATE_LOCALES[match.code]}${GREGORIAN}`;

  const base = normalized.split('-')[0];
  const byBase = SUPPORTED_LANGUAGES.find((l) => l.code.toLowerCase() === base);
  return byBase ? `${DATE_LOCALES[byBase.code]}${GREGORIAN}` : FALLBACK;
};
